import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { allArticles } from '@/mocks/siteData';
import { articleContents } from '@/mocks/articleContents';

const articleImageMap: Record<string, string> = {
  'article-1': 'https://readdy.ai/api/search-image?query=Japanese%20animation%20studio%20workspace%20with%20drawing%20table%20and%20storyboard%20frames%20on%20wall%2C%20warm%20ambient%20lighting%2C%20professional%20anime%20production%20environment%2C%20clean%20and%20organized%2C%20editorial%20photography%20with%20soft%20shadows&width=1200&height=630&seq=article-full-def-01&orientation=landscape',
  'article-2': 'https://readdy.ai/api/search-image?query=Retro%20Japanese%20VHS%20tape%20collection%20displayed%20on%20wooden%20shelves%20with%20vintage%20anime%20artwork%20covers%2C%201980s%20aesthetic%2C%20warm%20nostalgic%20lighting%2C%20cultural%20archival%20photography&width=1200&height=630&seq=article-full-hist-01&orientation=landscape',
  'article-3': 'https://readdy.ai/api/search-image?query=Japanese%20Akihabara%20district%20with%20anime%20posters%20and%20electronics%20stores%2C%20urban%20cultural%20landscape%2C%20warm%20evening%20neon%20glow%2C%20documentary%20style%20photography%20with%20cinematic%20composition&width=1200&height=630&seq=article-full-cult-01&orientation=landscape',
  'article-4': 'https://readdy.ai/api/search-image?query=Professional%20animation%20studio%20interior%20with%20digital%20drawing%20tablets%20and%20color%20scripts%20displayed%2C%20clean%20modern%20workspace%2C%20soft%20diffused%20lighting%2C%20editorial%20photography&width=1200&height=630&seq=article-full-ind-01&orientation=landscape',
  'article-5': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20home%20viewing%20setup%20with%20large%20monitor%20and%20comfortable%20lighting%2C%20clean%20aesthetic%20room%2C%20soft%20warm%20ambient%20light%2C%20editorial%20interior%20photography&width=1200&height=630&seq=article-full-guide-01&orientation=landscape',
};

export default function ArticlePage() {
  const { articleSlug } = useParams<{ articleSlug: string }>();
  const article = allArticles.find((a) => a.slug === articleSlug);

  if (!article) {
    return (
      <Layout>
        <div className="py-20 text-center">
          <i className="ri-error-warning-line text-4xl text-foreground-300 mb-4 block"></i>
          <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h1>
          <Link to="/categories" className="text-primary-600 hover:text-primary-700 text-sm">返回分类浏览</Link>
        </div>
      </Layout>
    );
  }

  const contentHtml = articleContents[article.id] || article.content || '';
  const relatedArticles = allArticles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: '里番文化研究所',
    },
    publisher: {
      '@type': 'Organization',
      name: '里番文化研究所',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://example.com/articles/${article.slug}`,
    },
    image: articleImageMap[article.id] || '',
  };

  const headerImage = articleImageMap[article.id] || '';

  return (
    <Layout>
      <SEOHead
        title={`${article.title} · 里番文化研究所`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://example.com/articles/${article.slug}`}
        ogType="article"
        ogImage={headerImage}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <article className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '分类浏览', to: '/categories' },
              { label: article.categoryName, to: `/categories/${article.categoryId}` },
              { label: article.title },
            ]}
          />

          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <Link
                to={`/categories/${article.categoryId}`}
                className="text-xs text-primary-600 bg-primary-100 px-3 py-1.5 rounded-full hover:bg-primary-200 transition-colors"
              >
                {article.categoryName}
              </Link>
              <span className="text-xs text-foreground-400">{article.readTime} 分钟阅读</span>
              <span className="text-xs text-foreground-400">{article.date}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-5 leading-tight">
              {article.title}
            </h1>
            <p className="text-foreground-500 text-base leading-relaxed">
              {article.excerpt}
            </p>
          </header>

          {headerImage && (
            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={headerImage}
                alt={article.title}
                title={`${article.title} - 里番文化研究所`}
                className="w-full aspect-[2/1] object-cover"
              />
            </div>
          )}

          <div
            className="prose-custom"
            dangerouslySetInnerHTML={{ __html: contentHtml }}
          />

          <div className="mt-10 pt-8 border-t border-background-200/70">
            <div className="flex flex-wrap gap-2 mb-8">
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-3 py-1.5 rounded-full">
                  #{tag}
                </span>
              ))}
            </div>

            {relatedArticles.length > 0 && (
              <div>
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">相关文章</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/articles/${rel.slug}`}
                      className="group bg-background-50 border border-background-200/70 rounded-lg p-4 hover:border-primary-200/70 transition-all duration-300"
                    >
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {rel.title}
                      </h4>
                      <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2">
                        {rel.excerpt}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </article>
    </Layout>
  );
}